import { useEffect, useState, useRef } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useForm } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { EmployeeDefaultValues } from "@shared/schema";
import { EMPLOYMENT_TYPES, US_STATES } from "@/lib/usStates";
import { EMPLOYEE_STATUS_OPTIONS } from "@/lib/employeeStatus";
import { PAY_CYCLE_OPTIONS, COMPENSATION_TYPE_OPTIONS } from "@/lib/employeeDefaults";
import { useEmployeeDefaultSettings } from "@/hooks/useEmployeeDefaultSettings";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { shouldOfferPayCycleApply, filterActiveEmployees, resolveApplyTargetIds } from "@/lib/payCycleApplyDecision";
import { Loader2, Settings2 } from "lucide-react";

type OwnerType = "agency" | "client" | "prospect";

type FieldKey =
  | "employmentType"
  | "payCycle"
  | "compensationType"
  | "status"
  | "city"
  | "state"
  | "county"
  | "country";

type FieldDefinition =
  | {
      key: FieldKey;
      label: string;
      type: "select";
      options: { value: string; label: string }[];
    }
  | {
      key: FieldKey;
      label: string;
      type: "text";
      placeholder?: string;
    };

const FIELD_DEFINITIONS: FieldDefinition[] = [
  {
    key: "employmentType",
    label: "Employment Type",
    type: "select",
    options: EMPLOYMENT_TYPES.map((value) => ({ value, label: value })),
  },
  {
    key: "payCycle",
    label: "Pay Cycle",
    type: "select",
    options: PAY_CYCLE_OPTIONS,
  },
  {
    key: "compensationType",
    label: "Compensation Type",
    type: "select",
    options: COMPENSATION_TYPE_OPTIONS,
  },
  {
    key: "status",
    label: "Status",
    type: "select",
    options: EMPLOYEE_STATUS_OPTIONS.map((option) => ({
      value: option.value,
      label: option.label,
    })),
  },
  {
    key: "city",
    label: "City",
    type: "text",
    placeholder: "Primary work city",
  },
  {
    key: "state",
    label: "State",
    type: "select",
    options: US_STATES.map((state) => ({
      value: state.code,
      label: `${state.name} (${state.code})`,
    })),
  },
  {
    key: "county",
    label: "County",
    type: "text",
    placeholder: "County",
  },
  {
    key: "country",
    label: "Country",
    type: "text",
    placeholder: "Country",
  },
];

const EMPLOYMENT_FIELD_KEYS: FieldKey[] = [
  "employmentType",
  "payCycle",
  "compensationType",
  "status",
];
const LOCATION_SELECT_KEYS: FieldKey[] = ["state"];
const LOCATION_TEXT_KEYS: FieldKey[] = ["city", "county", "country"];

const EMPLOYMENT_SELECT_DEFINITIONS = FIELD_DEFINITIONS.filter(
  (
    definition
  ): definition is Extract<FieldDefinition, { type: "select" }> =>
    definition.type === "select" &&
    EMPLOYMENT_FIELD_KEYS.includes(definition.key)
);

const LOCATION_SELECT_DEFINITIONS = FIELD_DEFINITIONS.filter(
  (
    definition
  ): definition is Extract<FieldDefinition, { type: "select" }> =>
    definition.type === "select" &&
    LOCATION_SELECT_KEYS.includes(definition.key)
);

const LOCATION_TEXT_DEFINITIONS = FIELD_DEFINITIONS.filter(
  (definition): definition is Extract<FieldDefinition, { type: "text" }> =>
    definition.type === "text" &&
    LOCATION_TEXT_KEYS.includes(definition.key)
);

const NO_DEFAULT_OPTION_VALUE = "__no_default__";

const buildDefaultFormValues = (
  defaults: Partial<EmployeeDefaultValues>
): Record<FieldKey, string> => {
  const initial: Record<FieldKey, string> = {} as Record<FieldKey, string>;

  FIELD_DEFINITIONS.forEach(({ key }) => {
    initial[key] = defaults[key]?.trim() ?? "";
  });

  return initial;
};

const buildDisplayValue = (
  defaults: Partial<EmployeeDefaultValues>,
  field: FieldDefinition
) => {
  const rawValue = defaults[field.key]?.trim();
  if (!rawValue) {
    return "Not set";
  }

  if (field.type === "select") {
    const match = field.options.find((option) => option.value === rawValue);
    return match ? match.label : rawValue;
  }

  return rawValue;
};

interface EmployeeDefaultSettingsCardProps {
  ownerType: OwnerType;
  ownerId?: string;
  heading: string;
  description?: string;
}

export function EmployeeDefaultSettingsCard({
  ownerType,
  ownerId,
  heading,
  description,
}: EmployeeDefaultSettingsCardProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    defaults,
    isLoading,
    isUpdating,
    updateDefaults,
  } = useEmployeeDefaultSettings(ownerType, ownerId);

  // Defaults only prefill new employees; this offers to push a changed pay cycle onto existing ones.
  const [applyPayCycle, setApplyPayCycle] = useState<{ oldValue: string; newValue: string } | null>(null);
  const [applyMode, setApplyMode] = useState<"all" | "choose" | "new">("new");
  const [applySelected, setApplySelected] = useState<Set<number>>(new Set());
  const [isApplying, setIsApplying] = useState(false);

  const employeesQuery = useQuery<Array<{ id: number; firstName: string; lastName: string; payCycle?: string | null; status?: string | null }>>({
    queryKey: ["/api/clients", ownerId, "employees"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/clients/${ownerId}/employees`);
      return res.json();
    },
    enabled: !!applyPayCycle && ownerType === "client" && !!ownerId,
  });
  const activeEmployees = filterActiveEmployees(employeesQuery.data ?? []);

  const closeApplyPayCycle = () => {
    setApplyPayCycle(null);
    setApplyMode("new");
    setApplySelected(new Set());
  };

  const handleApplyPayCycle = async () => {
    if (!applyPayCycle) return;
    const ids = resolveApplyTargetIds({
      mode: applyMode,
      activeEmployeeIds: activeEmployees.map((e) => e.id),
      selectedIds: applySelected,
    });
    if (ids.length === 0) {
      closeApplyPayCycle();
      return;
    }
    setIsApplying(true);
    // ponytail: one PATCH per employee, add a bulk endpoint if clients get large
    const results = await Promise.allSettled(
      ids.map((id) => apiRequest("PATCH", `/api/employees/${id}`, { payCycle: applyPayCycle.newValue }))
    );
    setIsApplying(false);
    const failed = results.filter((r) => r.status === "rejected").length;
    // Detail pages use their own per-employee keys, not the client list key.
    queryClient.invalidateQueries({ queryKey: ["/api/clients", ownerId, "employees"] });
    ids.forEach((id) => {
      queryClient.invalidateQueries({ queryKey: [`/api/employees/${id}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/employees", id, "benefits"] });
      queryClient.invalidateQueries({ queryKey: ["/api/enrollments", "employee", id] });
    });
    toast({
      title: failed === 0 ? "Pay cycle updated" : "Pay cycle partially updated",
      description:
        failed === 0
          ? `Updated ${ids.length} employee${ids.length === 1 ? "" : "s"}.`
          : `Updated ${ids.length - failed} of ${ids.length} - ${failed} failed.`,
      variant: failed === 0 ? undefined : "destructive",
    });
    closeApplyPayCycle();
  };

  const toggleSelected = (id: number) => {
    setApplySelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const form = useForm<Record<FieldKey, string>>({
    defaultValues: buildDefaultFormValues(defaults),
  });

  // Use ref to track if we've already reset to prevent infinite loops
  const previousDefaultsRef = useRef<string>();

  useEffect(() => {
    const defaultsString = JSON.stringify(defaults);
    if (previousDefaultsRef.current !== defaultsString) {
      form.reset(buildDefaultFormValues(defaults));
      previousDefaultsRef.current = defaultsString;
    }
  }, [defaults]);

  const handleSave = async (values: Record<FieldKey, string>) => {
    try {
      const payload: Partial<EmployeeDefaultValues> = {};
      FIELD_DEFINITIONS.forEach(({ key }) => {
        const rawValue = values[key];
        const trimmedValue =
          typeof rawValue === "string" ? rawValue.trim() : "";
        payload[key] = trimmedValue;
      });

      const oldPayCycle = (defaults.payCycle ?? "").trim();
      const newPayCycle = (payload.payCycle ?? "").trim();

      await updateDefaults(payload);
      toast({
        title: "Defaults updated",
        description: "New employees will use these field values automatically.",
      });
      setDialogOpen(false);

      if (shouldOfferPayCycleApply({ ownerType, ownerId, oldValue: oldPayCycle, newValue: newPayCycle })) {
        setApplyPayCycle({ oldValue: oldPayCycle, newValue: newPayCycle });
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to update defaults";
      toast({
        title: "Update failed",
        description: message,
        variant: "destructive",
      });
    }
  };

  const renderSelectField = (
    definition: Extract<FieldDefinition, { type: "select" }>
  ) => (
    <FormField
      key={definition.key}
      control={form.control}
      name={definition.key}
      render={({ field: controller }) => (
        <FormItem>
          <FormLabel>{definition.label}</FormLabel>
          <FormControl>
            <Select
              value={
                controller.value && controller.value.trim().length > 0
                  ? controller.value
                  : NO_DEFAULT_OPTION_VALUE
              }
              onValueChange={(nextValue) =>
                controller.onChange(
                  nextValue === NO_DEFAULT_OPTION_VALUE ? "" : nextValue
                )
              }
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={`Select ${definition.label.toLowerCase()}`}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_DEFAULT_OPTION_VALUE}>No default</SelectItem>
                {definition.options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormControl>
        </FormItem>
      )}
    />
  );

  const renderTextField = (
    definition: Extract<FieldDefinition, { type: "text" }>
  ) => (
    <FormField
      key={definition.key}
      control={form.control}
      name={definition.key}
      render={({ field: controller }) => (
        <FormItem>
          <FormLabel>{definition.label}</FormLabel>
          <FormControl>
            <Input
              {...controller}
              placeholder={definition.placeholder}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <div>
          <CardTitle>{heading}</CardTitle>
          {description ? (
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          ) : null}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDialogOpen(true)}
          disabled={isLoading}
        >
          <Settings2 className="mr-2 h-4 w-4" />
          Configure
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading defaults...
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {FIELD_DEFINITIONS.map((field) => (
              <div
                key={field.key}
                className="flex items-center justify-between gap-4 text-sm"
              >
                <span className="text-muted-foreground">{field.label}</span>
                <span className="font-medium">
                  {buildDisplayValue(defaults, field)}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Employee Defaults</DialogTitle>
            {description ? (
              <p className="text-sm text-muted-foreground">{description}</p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Choose values that should prefill new employee records for this
                {ownerType === "agency" ? " agency" : ""}.
              </p>
            )}
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSave)}
              className="space-y-4"
            >
              <div className="space-y-3">
                <p className="text-sm font-semibold text-muted-foreground">
                  Employment Defaults
                </p>
                <div className="grid gap-3 md:grid-cols-2">
                  {EMPLOYMENT_SELECT_DEFINITIONS.map(renderSelectField)}
                </div>
              </div>
              <div className="space-y-3">
                <p className="text-sm font-semibold text-muted-foreground">
                  Location Defaults
                </p>
                <div className="grid gap-3 md:grid-cols-2">
                  {LOCATION_SELECT_DEFINITIONS.map(renderSelectField)}
                  {LOCATION_TEXT_DEFINITIONS.map(renderTextField)}
                </div>
              </div>
              <DialogFooter className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    form.reset(buildDefaultFormValues(defaults));
                    setDialogOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isUpdating}>
                  {isUpdating ? "Saving..." : "Save Defaults"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!applyPayCycle} onOpenChange={(open) => { if (!open) closeApplyPayCycle(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Apply the new pay cycle to existing employees?</DialogTitle>
            <p className="text-sm text-muted-foreground">
              The default pay cycle changed to {applyPayCycle?.newValue}. Existing employees keep
              whatever pay cycle they already have unless you update them here.
            </p>
          </DialogHeader>

          {employeesQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading employees...
            </div>
          ) : activeEmployees.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No active employees need updating.</p>
          ) : (
            <RadioGroup value={applyMode} onValueChange={(v) => setApplyMode(v as typeof applyMode)} className="space-y-3">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="all" id="pay-cycle-apply-all" />
                <label htmlFor="pay-cycle-apply-all" className="text-sm">
                  Apply to all {activeEmployees.length} existing employees
                </label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="choose" id="pay-cycle-apply-choose" />
                <label htmlFor="pay-cycle-apply-choose" className="text-sm">
                  Choose which employees to update
                </label>
              </div>
              {applyMode === "choose" && (
                <ScrollArea className="h-48 rounded border p-2 ml-6">
                  <div className="space-y-2">
                    {activeEmployees.map((e) => (
                      <div key={e.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`pay-cycle-emp-${e.id}`}
                          checked={applySelected.has(e.id)}
                          onCheckedChange={() => toggleSelected(e.id)}
                        />
                        <label htmlFor={`pay-cycle-emp-${e.id}`} className="text-sm">
                          {e.firstName} {e.lastName}
                          <span className="text-muted-foreground ml-2">
                            (currently {e.payCycle?.trim() || "not set"})
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
              <div className="flex items-center gap-2">
                <RadioGroupItem value="new" id="pay-cycle-apply-new" />
                <label htmlFor="pay-cycle-apply-new" className="text-sm">
                  New employees only - don't change anyone existing
                </label>
              </div>
            </RadioGroup>
          )}

          <DialogFooter className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={closeApplyPayCycle}>
              Close
            </Button>
            <Button
              type="button"
              onClick={handleApplyPayCycle}
              disabled={isApplying || (applyMode === "choose" && applySelected.size === 0)}
            >
              {isApplying ? "Updating..." : applyMode === "new" ? "Done" : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
